import React from 'react';
import {
  Claim,
  ClaimItemResolvedStatusEnum,
  ClaimItemValues,
} from 'types/claim';
import { Text } from './Text';
import { CheckCircleIcon } from '@heroicons/react/outline';
import { Dropdown } from './Dropdown';
import { OrderDetail } from './OrderDetail';
import { ClaimResolutionOptions } from 'containers/NestedContainers/Claim';
import { useStore } from 'context/store-context';
import Panel from './Panel';

const EmptyRadioButton = () => (
  <div
    style={{ width: 25, height: 25, borderRadius: '50%', borderWidth: '1px' }}
  ></div>
);

const ClaimItemsList = ({
  claim,
  items,
  selectedItemId,
  resolution,
  onSelectAll,
  onSelectClaimItem,
  onSelectResolution,
  onShowResolutionAction,
  onViewResolution,
  onResolvePartialItems,
}: {
  claim: Claim | null;
  items: Claim['items'];
  selectedItemId?: string;
  onSelectAll?: () => void;
  onSelectClaimItem: (id: string | undefined) => void;
  resolution?: ClaimItemResolvedStatusEnum;
  onSelectResolution: (r: ClaimItemResolvedStatusEnum) => void;
  onShowResolutionAction: () => void;
  onViewResolution: (id: string) => void;
  onResolvePartialItems: (claimItem: ClaimItemValues) => void;
}) => {
  const { storeProperties } = useStore();
  const enableStoreCredit =
    storeProperties?.resolvePortalOptions?.enableStoreCredit || false;

  const ResolvedItems = (claim?.items || []).filter((i) => i.settlement);
  const ResolvableItems = (claim?.items || []).filter((i) => !i.settlement);
  const isAllSelected = ResolvableItems?.length === items?.length;
  const hasSingleItemSelected = items.length > 0;

  return (
    <div className="space-y-6">
      {/* Issue items */}
      <div>
        <Panel
          title="Issue items"
          headerClasses="bg-yellow-100 text-yellow-700"
        >
          <div className="px-3 py-3">
            {/* To Be Resolved Items */}
            {ResolvableItems.length > 0 ? (
              <div className="mb-4">
                <div className="flex justify-between items-center my-2">
                  <div
                    className="flex justify-start items-center w-40 cursor-pointer"
                    onClick={onSelectAll}
                  >
                    {isAllSelected ? (
                      <CheckCircleIcon color="blue" width={'25px'} />
                    ) : (
                      <EmptyRadioButton />
                    )}
                    <Text className="ml-2">Select All</Text>
                  </div>
                  <div>
                    {hasSingleItemSelected && (
                      <Dropdown
                        placeholder="Choose Resolution"
                        btnClassName="w-40 border rounded-sm"
                        options={ClaimResolutionOptions({
                          enableStoreCredit,
                        })}
                        onSelectOption={(o) => {
                          onShowResolutionAction();
                          onSelectResolution(o as ClaimItemResolvedStatusEnum);
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  {ResolvableItems.map((item) => {
                    const isItemSelected = items.find((i) => i.id === item.id);
                    return (
                      <div
                        key={item.id}
                        className="flex gap-3 p-3 bg-gray-50 rounded"
                      >
                        <div
                          className={'cursor-pointer mt-2'}
                          onClick={onSelectClaimItem.bind(null, item.id)}
                        >
                          {isItemSelected ? (
                            <CheckCircleIcon color="blue" width={'20px'} />
                          ) : (
                            <div className="w-5 h-5 border border-gray-300 rounded-full"></div>
                          )}
                        </div>
                        <OrderDetail
                          order={item.orderItem}
                          claimItem={item}
                          showClaimItemResolution={!hasSingleItemSelected}
                          resolution={
                            item.resolution ===
                            ClaimItemResolvedStatusEnum.IN_REVIEW
                              ? undefined
                              : (item.resolution as ClaimItemResolvedStatusEnum)
                          }
                          onViewResolution={onViewResolution.bind(
                            null,
                            item.id as string,
                          )}
                          disabledClamResolution
                          showCategories
                          selectedCategory={claim?.reason}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="px-5">All items are resolved</div>
            )}
          </div>
        </Panel>
        <Panel
          title="Resolution Summary"
          headerClasses="bg-green-100 text-green-700"
        >
          <div className="px-3 py-3">
            {/* Resolved Items */}
            {ResolvedItems.length > 0 ? (
              <div>
                <div className="space-y-2">
                  {ResolvedItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-3 p-3 bg-green-50 rounded"
                    >
                      <OrderDetail
                        order={item.orderItem}
                        claimItem={item}
                        showClaimItemResolution={!hasSingleItemSelected}
                        resolution={
                          item.resolution ===
                          ClaimItemResolvedStatusEnum.IN_REVIEW
                            ? undefined
                            : (item.resolution as ClaimItemResolvedStatusEnum)
                        }
                        onViewResolution={onViewResolution.bind(
                          null,
                          item.id as string,
                        )}
                        disabledClamResolution
                        showCategories
                        selectedCategory={claim?.reason}
                        onResolvePartialItems={onResolvePartialItems}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="px-5">No items are resolved</div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default ClaimItemsList;
